import { useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";

export const adminInputCls =
  "w-full rounded-lg border border-line bg-white px-4 py-3 text-base text-ink placeholder:text-slate-400 transition-colors focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30";
export const adminLabelCls = "mb-1.5 block font-accent text-xs font-semibold uppercase tracking-widest text-muted";

export const passwordStrength = (pw) => {
  let score = 0;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
};

const STRENGTH_LABELS = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong"];

export const PasswordInput = ({ label, testid, value, onChange, placeholder = "••••••••", showStrength = false }) => {
  const [visible, setVisible] = useState(false);
  const score = passwordStrength(value);
  return (
    <div>
      <label htmlFor={testid} className={adminLabelCls}>{label}</label>
      <div className="relative">
        <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          id={testid}
          data-testid={testid}
          type={visible ? "text" : "password"}
          required
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
          className={`${adminInputCls} pl-10 pr-12`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          data-testid={`${testid}-toggle`}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 transition-colors hover:text-navy"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {showStrength && value && (
        <div className="mt-2" data-testid={`${testid}-strength`}>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`h-1 flex-1 rounded-full ${i < score ? "bg-gold" : "bg-line"}`} />
            ))}
          </div>
          <p className="mt-1 text-xs text-muted">{STRENGTH_LABELS[score]} — needs 12+ chars, upper, lower, number, symbol</p>
        </div>
      )}
    </div>
  );
};

export const AdminCard = ({ children, title, subtitle }) => (
  <div className="w-full max-w-md rounded-xl border border-line bg-white p-10 shadow-soft">
    <div className="text-center">
      <span className="mx-auto inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 ring-1 ring-line">
        <img src="/logo.webp" alt="Student First Overseas" className="h-16 w-auto" />
      </span>
      <p className="mt-5 font-accent text-xs font-bold uppercase tracking-[0.3em] text-gold">Student First Overseas</p>
      <h1 className="mt-1.5 font-heading text-2xl font-extrabold text-navy">{title}</h1>
      {subtitle && <p className="mt-2 text-sm text-muted">{subtitle}</p>}
    </div>
    <div className="mt-8">{children}</div>
  </div>
);

export const AdminShell = ({ children }) => (
  <div className="flex min-h-[85vh] items-center justify-center bg-surface px-6 py-20">{children}</div>
);

export const formatApiError = (err, fallback = "Something went wrong. Please try again.") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "Invalid input").join(" ");
  return fallback;
};
